/**
 * URL Sanitizer Utility
 * 
 * This utility provides functions to safely handle URLs and prevent [object Object] errors
 * that can occur when objects are accidentally used in place of URL strings.
 * 
 * Created: May 2025 as part of the security and URL handling improvements
 */

/**
 * Safely converts any value to a valid URL string
 * Prevents the common [object Object] URL errors
 * 
 * @param url - The URL value to sanitize (could be string, object, null, etc.)
 * @param defaultUrl - The default URL to use if the provided URL is invalid
 * @returns A safe string URL
 */
export const sanitizeUrl = (url: any, defaultUrl: string = '/'): string => {
  // Handle null, undefined or false values
  if (url == null || url === false) {
    console.warn('URL Sanitizer: Received null/undefined/false URL value', { url });
    return defaultUrl;
  }

  // Handle string URLs (most common case)
  if (typeof url === 'string') {
    // Return empty strings as the default URL
    if (!url.trim()) {
      return defaultUrl;
    }
    return url;
  }

  // Handle number values by converting to string
  if (typeof url === 'number') {
    return String(url);
  }

  // Handle object URLs with some common patterns
  if (typeof url === 'object') {
    console.warn('URL Sanitizer: Received object instead of string URL', { url });
    
    // Handle objects with name or path properties (common pattern)
    if (url?.path) return url.path;
    if (url?.name) return url.name;
    if (url?.href) return url.href;
    if (url?.url) return url.url;
    
    // Last resort - avoid [object Object] by returning the default
    return defaultUrl;
  }

  // For any other unexpected type, return the default URL
  console.warn('URL Sanitizer: Received unexpected URL type', { url, type: typeof url });
  return defaultUrl;
};

/**
 * Check if a URL is internal to the application
 * 
 * @param url - The URL to check
 * @returns boolean - true if internal URL, false if external
 */
export const isInternalUrl = (url: string): boolean => {
  if (!url) return true;
  
  return !(url.startsWith('http://') || 
           url.startsWith('https://') || 
           url.startsWith('//') ||
           url.startsWith('tel:') ||
           url.startsWith('mailto:'));
};
